import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { images, coins as coinsApi, certificationServices, customFields } from '../../utils/api';
import { TableContext } from '../shared/table/context/TableContext';
import { EditModeProvider, EditModeContext } from '../shared/table/context/EditModeContext';
import { useNavigate } from 'react-router-dom';
import BaseTable from '../shared/table/BaseTable';
import { useColumns, useRows } from './hooks/useColumns';
import CoinListToolbar from './CoinListToolbar';
import CoinCard from './CoinCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Inner component that has access to EditModeContext
const TableContent = ({
  coins,
  selectedCoins,
  onToggleSelection,
  onSetSelection,
  showThumbnails,
  onCoinsUpdate,
  services,
  servicesLoaded,
  servicesError
}) => {
  const navigate = useNavigate();
  const { isEditMode } = useContext(EditModeContext);
  const { setColumns, filterRows } = useContext(TableContext);
  const { columns, customFieldValuesMap } = useColumns(coins, showThumbnails);

  // Update columns in context when they change
  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);
  
  const processedCoins = useRows(coins, customFieldValuesMap);
  const filteredCoins = filterRows(processedCoins);

  const handleRowClick = useCallback((id, button) => {
    if (button === 0) { // Left click
      navigate(`/coins/${id}`);
    } else if (button === 1) { // Middle click
      window.open(`/coins/${id}`, '_blank');
    }
  }, [navigate]);

  // Memoize selectedItems to prevent unnecessary re-renders and ensure string IDs
  const memoizedSelectedItems = useMemo(() => {
    return new Set([...selectedCoins].map(id => String(id)));
  }, [selectedCoins]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <CoinListToolbar
          title="Inventory"
          columns={columns}
          data={coins}
          selectedItems={memoizedSelectedItems}
          showQuickFilter={true}
          quickFilterProps={{ debounceMs: 300 }}
        />
      </div>
      <div className="flex-1 min-h-0">
        {isMobile ? (
          <div className="p-3 space-y-3 bg-gray-50">
            {filteredCoins.map(coin => (
              <CoinCard
                key={coin.id}
                coin={coin}
                isSelected={selectedCoins.has(String(coin.id))}
                onSelect={onToggleSelection}
              />
            ))}
          </div>
        ) : (
          <BaseTable
            data={processedCoins}
            columns={columns}
            selectedItems={memoizedSelectedItems}
            onSelectionChange={onSetSelection}
            onRowClick={handleRowClick}
            showThumbnails={showThumbnails}
            renderCustomRow={null}
          />
        )}
      </div>
    </div>
  );
};

// Outer component that manages services state and provides EditModeContext
const CoinListTable = ({
  coins = [],
  selectedCoins = new Set(),
  onToggleSelection,
  onSetSelection,
  showThumbnails = true,
  onCoinsUpdate
}) => {
  const [services, setServices] = useState([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [servicesError, setServicesError] = useState(null);

  // Function to load certification services
  const loadServices = useCallback(async () => {
    try {
      console.log('Loading certification services...');
      const result = await certificationServices.getAll();
      console.log('Loaded certification services:', result);
      
      if (Array.isArray(result) && result.length > 0) {
        setServices(result);
        setServicesLoaded(true);
        setServicesError(null);
      } else {
        console.error('Received empty or invalid certification services array:', result);
        setServicesError('No certification services available');
        setServicesLoaded(false);
      }
    } catch (err) {
      console.error('Failed to load certification services:', err);
      setServicesError(err.message);
      setServicesLoaded(false);
    }
  }, []);

  // Handler for saving edited cells
  const handleSaveChanges = useCallback(async (changes) => {
    try {
      // Prevent saves if services haven't loaded
      if (!servicesLoaded) {
        console.error('Cannot save changes: certification services not loaded');
        if (servicesError) {
          console.error('Services error:', servicesError);
        }
        return false;
      }

      console.log('Received changes:', changes);
      
      // Group changes by coin ID and type (regular vs custom fields)
      const changesByCoin = changes.reduce((acc, change) => {
        const { id, field, value } = change;
        console.log('Processing change:', { id, field, value });
        
        // Initialize coin changes if needed
        if (!acc[id]) {
          acc[id] = {
            regular: {},
            customFields: {}
          };
        }
        
        // Handle custom fields
        if (field.startsWith('custom_field_')) {
          const fieldName = field.replace('custom_field_', '');
          acc[id].customFields[fieldName] = value === '' ? null : value;
        }
        // Special handling for TPG field
        else if (field === 'certification_service_name') {
          // Find the service by name (case-insensitive)
          const service = services.find(s => s.name.toLowerCase() === value.toLowerCase());
          if (service) {
            acc[id].regular['certification_service_id'] = service.id;
            console.log(`Found certification service: ${service.name} (ID: ${service.id})`);
          } else {
            console.error(`No matching certification service found for: ${value}`);
            acc[id].regular['certification_service_id'] = null;
          }
        } else {
          acc[id].regular[field] = value === '' ? null : value; // Convert empty string to null
        }
        return acc;
      }, {});

      // Update coins sequentially to avoid token mismatch
      for (const [coinId, updates] of Object.entries(changesByCoin)) {
        console.log(`Updating coin ${coinId} with:`, updates);
        
        // Update regular fields if any exist
        if (Object.keys(updates.regular).length > 0) {
          await coinsApi.update(coinId, updates.regular);
        }
        
        // Update custom fields if any exist
        if (Object.keys(updates.customFields).length > 0) {
          await customFields.setValues(coinId, updates.customFields);
        }
      }

      console.log('All updates completed successfully');
      
      // Get fresh data after saving changes
      if (onCoinsUpdate) {
        try {
          const freshData = await coinsApi.getAll();
          console.log('Fetched fresh data after save:', freshData);
          onCoinsUpdate(freshData);
        } catch (error) {
          console.error('Failed to fetch fresh data after save:', error);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save changes:', error);
      return false;
    }
  }, [services, servicesLoaded, servicesError, onCoinsUpdate]);

  // Load services when edit mode is enabled and handle retries
  const handleEditModeChange = useCallback((isEditMode) => {
    if (isEditMode && !servicesLoaded) {
      let retryInterval;
      
      const startRetrying = () => {
        if (!retryInterval) {
          console.log('Starting retry interval...');
          retryInterval = setInterval(async () => {
            console.log('Retrying certification services load...');
            try {
              await loadServices();
              if (servicesLoaded) {
                console.log('Services loaded successfully, clearing retry interval');
                clearInterval(retryInterval);
                retryInterval = null;
              }
            } catch (error) {
              console.error('Failed to load services:', error);
            }
          }, 5000);
        }
      };

      // Initial load attempt
      loadServices().catch(error => {
        console.error('Initial load failed:', error);
        startRetrying();
      });

      // Cleanup function
      return () => {
        if (retryInterval) {
          clearInterval(retryInterval);
          retryInterval = null;
        }
      };
    }
  }, [servicesLoaded, loadServices]);

  return (
    <EditModeProvider 
      onSave={handleSaveChanges}
      onEditModeChange={handleEditModeChange}
    >
      <TableContent
        coins={coins}
        selectedCoins={selectedCoins}
        onToggleSelection={onToggleSelection}
        onSetSelection={onSetSelection}
        showThumbnails={showThumbnails}
        onCoinsUpdate={onCoinsUpdate}
        services={services}
        servicesLoaded={servicesLoaded}
        servicesError={servicesError}
      />
    </EditModeProvider>
  );
};

// Don't memoize the component since we need it to re-render when density changes
export default CoinListTable;
